/**
 * This hook is used to determine if a learner is eligible to see the Khanmigo
 * promo in the learner dashboard side navigation.
 */
import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {useQuery} from "@khan/data-gql";
import {RequestInfo} from "@khan/shared-core";

import useProfile from "#data-access/use-profile.ts";
import {isConsideredOver18} from "#shared-package/is-over-18.ts";

import {LEARNER_KHANMIGO_ELIGIBILITY_QUERY} from "./queries.ts";

export enum Status {
    LOADING = "loading",
    ERROR = "error",
    SUCCESS = "success",
}

export type LearnerKhanmigoEligibility = {
    isEligible: boolean | null;
    status: Status;
};

const useGetLearnerDashboardPromoEligibility =
    (): LearnerKhanmigoEligibility => {
        const {remoteCountry, kaLocale} = RequestInfo;
        const countryAndLocaleIsEligible =
            remoteCountry === "US" && kaLocale === "en";
        const profileResult = useProfile();
        const [result] = useQuery(LEARNER_KHANMIGO_ELIGIBILITY_QUERY, {
            variables: {},
            fetchPolicy: FetchPolicy.CacheAndNetwork,
            // We're going to skip the query if we know the user is ineligible
            // based on country/locale data.
            skip: !countryAndLocaleIsEligible,
        });

        // Return early if we know the user is ineligible based on
        // country/locale data.
        if (!countryAndLocaleIsEligible) {
            return {isEligible: false, status: Status.SUCCESS};
        }

        const {profile} = profileResult;

        if (
            profileResult.status === Status.LOADING ||
            result.status === Status.LOADING
        ) {
            return {isEligible: null, status: Status.LOADING};
        }

        if (
            profileResult.status !== Status.SUCCESS ||
            result.status !== Status.SUCCESS ||
            !result.data?.user ||
            !profile
        ) {
            return {isEligible: false, status: Status.ERROR};
        }

        const {aiGuideDisabledReason, aiGuideActiveEnrollmentGroups} =
            result.data.user;
        const hasActiveKhanmigoAccess =
            aiGuideActiveEnrollmentGroups &&
            aiGuideActiveEnrollmentGroups.length > 0;

        const isEligible =
            !hasActiveKhanmigoAccess &&
            aiGuideDisabledReason !== "MODERATION" &&
            isConsideredOver18(profile);

        return {isEligible, status: Status.SUCCESS};
    };

export default useGetLearnerDashboardPromoEligibility;
